import React, { FC, useState, useEffect, useRef } from "react";
import { Enums, String, Constants, Utilities, moment} from "@utils";
import {
  CheckBox,
  TextBox,
  Validator,
  NumberBox,
  DateBox,
  RadioGroup,
} from "devextreme-react";
import { RequiredRule, StringLengthRule } from "devextreme-react/data-grid";
import { AEASelect, AEATextArea } from "@components/";

export interface AEAItemsSelect {
  id: string;
  label: string;
}
export interface AEAFieldEditorProps {
  fieldType: Enums.FieldTypes | string;
  id: string;
  readOnly?: boolean;
  placeholder?: string;
  requiredErrorMessage?: string;
  required?: boolean;
  multiple?: boolean;
  items?: AEAItemsSelect[];
  layout?: string;
  maxLength?: number;
  lengthRuleMessage?: string;
  defaultValue?: any;
  onValueChanged?(value: any): void;
  value?: any;
  minDate?: Date;
  maxDate?: Date;
  maxValue?: number;
  minValue?: number;
  numberFormat?: string;
  acceptCustomValue?: boolean;
}
const AEAFieldEditor: FC<AEAFieldEditorProps> = (props: AEAFieldEditorProps) => {
  const [value, setValue] = useState(props.defaultValue);

  useEffect(() => {
    setValue(props.value);
  }, [props.value]);

  const onValueChanged = (_value: any) => {
    setValue(_value.value);
    if (!Utilities.isNullObject(props.onValueChanged)) {
      props.onValueChanged(_value.value);
    }
  };
  const getRequiredRule = () => {
    return props.required ? (
      <RequiredRule message={props.requiredErrorMessage} />
    ) : (
      String.Empty
    );
  };
  const getStringLengthRule = () => {
    return props.maxLength ? (
      <StringLengthRule
        message={props.lengthRuleMessage}
        max={props.maxLength}
      />
    ) : (
      String.Empty
    );
  };
  const validateCurrentValue = (): boolean => {
    return !Utilities.isNullObject(value) && !String.IsNullOrWhiteSpace(value);
  };
  const getDateRender = () => { 
    const _value = value !== null && value !== undefined && value !== String.Empty 
      ? moment.utc(value).tz(Constants.GeneralFormats.TIME_ZONE).format(Constants.GeneralFormats.DATE_FORMAT)    
      : value;    
    return (
      <DateBox
        defaultValue={_value}
        onValueChanged={onValueChanged}
        adaptivityEnabled
        pickerType={Enums.PickerTypes.Calendar}
        type={Enums.FieldTypes.Date}
        acceptCustomValue={false}
        readOnly={props.readOnly}
        key={props.id}
        id={props.id}
        placeholder={props.placeholder}
        value={_value}
        displayFormat={"dd/MM/yyyy"}
        min={props.minDate}
        max={props.maxDate}        
      >
        <Validator>{getRequiredRule()}</Validator>
      </DateBox>
    );
  };
  const getNumberRender = () => {
    return (
      <NumberBox
        defaultValue={value}
        onValueChanged={onValueChanged}
        readOnly={props.readOnly}
        key={props.id}
        id={props.id}
        placeholder={props.placeholder}
        format={Constants.GeneralFormats.NUMBER_FORMAT}
        value={value}
        max={props.maxValue}
        min={props.minValue}
      >
        <Validator>{getRequiredRule()}</Validator>
      </NumberBox>
    );
  };
  const getCurrencyRender = () => {
    return (
      <NumberBox
        defaultValue={value}
        onValueChanged={onValueChanged}
        readOnly={props.readOnly}
        key={props.id}
        id={props.id}
        placeholder={props.placeholder}
        format={Constants.GeneralFormats.CURRENCY_FORMAT}
        value={value}
        max={props.maxValue}
        min={props.minValue}
      >
        <Validator>{getRequiredRule()}</Validator>
      </NumberBox>
    );
  };
  const getPercentageRender = () => {
    return (
      <NumberBox
        defaultValue={value}
        onValueChanged={onValueChanged}
        readOnly={props.readOnly}
        key={props.id}
        id={props.id}
        placeholder={props.placeholder}
        format={Constants.GeneralFormats.PERCENTAGE_FORMAT}
        value={value}
        max={props.maxValue}
        min={props.minValue}
      >
        <Validator>{getRequiredRule()}</Validator>
      </NumberBox>
    );
  };
  const getSelectRender = () => {
    return (
      <AEASelect
        onValueChanged={(valueSelection) =>
          onValueChanged({ value: valueSelection })
        }
        defaultSelection={Utilities.isNullObject(value) ? [] : value}
        items={props.items}
        displayExpr="label"
        keyExpr="id"
        id={props.id}
        key={props.id}
        multiple={props.multiple}
        placeholder={props.placeholder}
        readOnly={props.readOnly}
        acceptCustomValue={props.acceptCustomValue}
      >
        <Validator>{getRequiredRule()}</Validator>
      </AEASelect>
    );
  };
  const getRadio = () => {
    const _value = Utilities.isNullObject(props.value)
      ? props.items[0].id
      : props.value;
    return (
      <RadioGroup
        key={props.id}
        id={props.id}
        valueExpr={"id"}
        displayExpr={"label"}
        dataSource={props.items}
        layout={props.layout as any}
        value={_value}
        defaultValue={_value}
        readOnly={props.readOnly}
        onValueChanged={onValueChanged}
      >
        <Validator>{getRequiredRule()}</Validator>
      </RadioGroup>
    );
  };
  const getTextRender = () => {
    return (
      <TextBox
        defaultValue={
          Array.isArray(props.defaultValue) ? null : props.defaultValue
        }
        onValueChanged={onValueChanged}
        readOnly={props.readOnly}
        key={props.id}
        id={props.id}
        placeholder={props.placeholder}
        value={Array.isArray(value) ? null : value}
      >
        <Validator>
          {getRequiredRule()}
          {getStringLengthRule()}
        </Validator>
      </TextBox>
    );
  };
  const getUserRender = () => {
    return <div></div>;
  };
  const getChekBox = () => {
    return (
      <CheckBox
        value={value}
        onValueChanged={onValueChanged}
        readOnly={props.readOnly}
        key={props.id}
        id={props.id}
      ></CheckBox>
    );
  };
  const getTextAreaRender = () => {
    return (
      <AEATextArea
        defaultValue={props.defaultValue}
        onValueChanged={onValueChanged}
        readOnly={props.readOnly}
        key={props.id}
        id={props.id}
        placeholder={props.placeholder}
        value={value}
        maxLength={props.maxLength}
      >
        <Validator>{getRequiredRule()}</Validator>
      </AEATextArea>
    );
  };
  const getRender = () => {      
    switch (props.fieldType) {
      case Enums.FieldTypes.Date:
        return getDateRender();
      case Enums.FieldTypes.Number:
        return getNumberRender();
      case Enums.FieldTypes.Select:
        return getSelectRender();
      case Enums.FieldTypes.Text:
        return getTextRender();
      case Enums.FieldTypes.User:
        return getUserRender();
      case Enums.FieldTypes.Checkbox:
        return getChekBox();
      case Enums.FieldTypes.currency:
        return getCurrencyRender();
      case Enums.FieldTypes.Percentage:
        return getPercentageRender();
      case Enums.FieldTypes.Textarea:
        return getTextAreaRender();
      case Enums.FieldTypes.Radio:
        return getRadio();
    }
  };
  return getRender();
};

export default AEAFieldEditor;
